import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  Input,
  Select,
  Row,
  Col,
  InputNumber,
  Button,
  Tooltip,
  Typography,
  Radio,
  DatePicker,
} from 'antd';

import { CloseOutlined } from '@ant-design/icons';
import { sortBy } from 'lodash';

import {
  Form,
  WrapperSelect,
  ThumbnailItem,
  FullWidthSpace,
  Fieldset,
} from 'common/components';
import BrickCodeInput from './BrickCodeInput';
import ProductEditBrandModal from 'pages/product-full-view/components/product-edit-brand/ProductEditBrandModal';

import * as companySelectors from 'pages/company-profile/controllers/selectors';

import { useGetMemberInfo } from './hooks';
import {
  DATE_FORMAT,
  PACKAGE_LEVELS,
  STATUS_PRODUCT,
} from 'static/Constants';
import { useCheckIsProductOverlay } from 'hooks/product';
import { useGetEnumsByEnumName } from 'hooks';

import { useIntl } from 'react-intl';
import messages from 'i18n/messages/product';

import { OverlayInputWrapper } from '../overlay';

import { get, isEqual } from 'lodash';

import { useGetMemberHeaderFromMemberId } from 'common/queries/member-module';
import { disabledPreviousDays } from 'utils/timezone';

import { ERROR_FIELD_NAME_COLLECTION } from '../preview-requirements/constants';
import {
  FormItemCategory,
  FormItemSubCategory,
  FormItemClass,
} from 'common/components/product-add/components/FormItemCategoryProduct';

import './CreateProductForm.less';

const { Option } = Select;
const { Text } = Typography;

const CreateProductForm = (props) => {
  const intl = useIntl();

  const {
    form,
    productData,
    formMode,
    brick,
    updateBrickCodeData,
    handleResetBrandId,
    setBrandId,
    editBrandVisible,
    setEditBrandVisible,
    brandThumb,
    setBrandThumb,
    errorInfo,
    memberIdBrand,
    originProductFull,
    productFull,
  } = props;

  const isProductOverlay = useCheckIsProductOverlay({
    productFull: formMode === 'edit' ? productFull : null,
  });

  const userMemberInfo =
    useSelector(companySelectors.selectMemberProfile()) ?? {};

  const { memberInfo: companyInfo } = useGetMemberInfo(memberIdBrand);
  const { data: memberHeaderOfProductOwner } = useGetMemberHeaderFromMemberId({
    memberId: productData?.ownerId,
    enabled: Boolean(productData && productData?.ownerId),
  });

  const isEdit = formMode === 'edit';

  const { data: memberHeaderOfProductCreator } = useGetMemberHeaderFromMemberId(
    {
      memberId: memberIdBrand,
      enabled: !!memberIdBrand,
    },
  );

  const handleNumberInput = (event) => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  const initFieldsError = () => {
    if (!errorInfo) return;

    form.setFields(errorInfo);
  };

  const isPrivateVisibilityProduct = () => {
    if (formMode === 'create') {
      if (companyInfo || memberIdBrand) {
        return (
          memberHeaderOfProductCreator?.products?.toLowerCase() === 'private'
        );
      }
    }

    if (formMode === 'edit') {
      return memberHeaderOfProductOwner?.products?.toLowerCase() === 'private';
    }

    //* default return;
    return userMemberInfo?.products?.toLowerCase() === 'private';
  };

  useEffect(() => {
    initFieldsError();
  }, []);

  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({
        packageLevel: PACKAGE_LEVELS[0],
        isPrivateLabel: true,
      });
    }
  }, [isEdit]);

  const brandError = errorInfo?.find((errorItem) =>
    errorItem.name.includes(ERROR_FIELD_NAME_COLLECTION.BRAND),
  );

  const brandErrorMessage = brandError ? brandError.errors[0] : '';
  const getOverlayProps = (path) => {
    const originValue = get(originProductFull, path);

    return {
      checkShowOverlayFn: ({ value }) => {
        return (
          !isEqual(value ?? null, originValue ?? null) &&
          isProductOverlay &&
          !!originProductFull
        );
      },
      originValue: originValue || 'N/A',
    };
  };

  const getBrickCodeOverlayProps = () => {
    const brickCode =
      brick?.brickCode?.toString() !== originProductFull?.brickCode?.toString();

    const originValue = originProductFull?.brickCode || 'N/A';
    return {
      showOverlay: isProductOverlay ? brickCode : null,
      originValue: isProductOverlay ? originValue : null,
    };
  };

  const renderProductFieldset = () => {
    return (
      <Row
        className='add-product__section-row'
        gutter={12}
        id='product-field'
      >
        <Col className='add-product__section-column' span={8}>
          <Fieldset title='Product'>
            <Form.Item
              label='Product Name'
              name='productName'
              rules={[
                {
                  required: true,
                  message: intl.formatMessage(messages.productNameRequired),
                },
              ]}
            >
              <OverlayInputWrapper {...getOverlayProps('productName')}>
                <Input />
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item
              label='Manufacturer'
              name='manufacturer'
              rules={[
                {
                  required: true,
                  message: 'Manufacturer is required',
                },
              ]}
            >
              <OverlayInputWrapper {...getOverlayProps('manufacturer')}>
                <Input />
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item
              label='Brand'
              name='brandsReportingToMasterAccount'
              rules={[
                {
                  required: true,
                  message: 'Brand is required',
                },
              ]}
            >
              <OverlayInputWrapper
                {...getOverlayProps('brandsReportingToMasterAccount')}
              >
                <Input />
              </OverlayInputWrapper>
            </Form.Item>

            <FormItemCategory />
            <FormItemSubCategory />
            <FormItemClass />

            <Form.Item name='visibility' label='Visibility'>
              <OverlayInputWrapper {...getOverlayProps('visibility')}>
                {isPrivateVisibilityProduct() ? (
                  <WrapperSelect
                    disabled={productData?.gdsnShield}
                    getPopupContainer={() =>
                      document.getElementById('product-field')
                    }
                  >
                    <Option value='Only If Shared'>Only If Shared</Option>
                    <Option value='Private'>Private</Option>
                  </WrapperSelect>
                ) : (
                  <WrapperSelect
                    disabled={productData?.gdsnShield}
                    getPopupContainer={() =>
                      document.getElementById('product-field')
                    }
                  >
                    <Option value='Only If Shared'>Only If Shared</Option>
                    <Option value='Public'>Public</Option>
                    <Option value='Private'>Private</Option>
                  </WrapperSelect>
                )}
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item name='status' label='Status'>
              <OverlayInputWrapper {...getOverlayProps('status')}>
                <WrapperSelect
                  getPopupContainer={() =>
                    document.getElementById('product-field')
                  }
                  disabled={productData?.status?.toLowerCase() === 'exception'}
                >
                  {sortBy([...STATUS_PRODUCT], ['displayName']).map(
                    (status, index) => (
                      <Option value={status?.value} key={index}>
                        {status?.displayName}
                      </Option>
                    ),
                  )}
                </WrapperSelect>
              </OverlayInputWrapper>
            </Form.Item>
          </Fieldset>

          <Fieldset title='Misc'>
            <Form.Item
              name='dateAvailableToDistribution'
              label='Date Available to Distribution'
            >
              <OverlayInputWrapper
                {...getOverlayProps('dateAvailableToDistribution')}
              >
                <DatePicker
                  disabledDate={disabledPreviousDays}
                  format={DATE_FORMAT.SYSTEM_FORMAT}
                  placeholder=''
                  style={styles.fullWidthInput}
                />
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item
              name='dateAvailableToRetail'
              label='Date Available to Retail'
            >
              <OverlayInputWrapper
                {...getOverlayProps('dateAvailableToRetail')}
              >
                <DatePicker
                  disabledDate={disabledPreviousDays}
                  format={DATE_FORMAT.SYSTEM_FORMAT}
                  placeholder=''
                  style={styles.fullWidthInput}
                />
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item name='brick' label='Brick Code'>
              <OverlayInputWrapper
                style={{ paddingRight: '10px' }}
                {...getBrickCodeOverlayProps()}
              >
                <BrickCodeInput
                  updateBrickCodeData={updateBrickCodeData}
                  initialValue={brick}
                />
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item label='Brick Code Name'>
              <OverlayInputWrapper {...getOverlayProps('brickCodeName')}>
                <Input value={brick?.brickCodeName} disabled />
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item name='talo' label='Talo'>
              <OverlayInputWrapper {...getOverlayProps('talo')}>
                <Radio.Group>
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item name='madeInTheUsa' label='Made in the USA'>
              <Radio.Group>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </Form.Item>
          </Fieldset>
        </Col>

        <Col className='add-product__section-column' span={8}>
          <Fieldset title='Identifiers'>
            <Form.Item name='customerSmu' label='Customer / SMU'>
              <OverlayInputWrapper {...getOverlayProps('customerSmu')}>
                <Radio.Group>
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item
              label='UPC'
              name='upc'
              rules={[{ required: true, message: 'UPC is required' }]}
            >
              <OverlayInputWrapper {...getOverlayProps('upc')}>
                <Input maxLength={MAX_NUMBER_INPUT_UPC} />
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item name='activeUpc' label='Active UPC'>
              <OverlayInputWrapper {...getOverlayProps('activeUpc')}>
                <Radio.Group>
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item label='Universal Product ID' name='universalProductId'>
              <OverlayInputWrapper {...getOverlayProps('universalProductId')}>
                <Input
                  min='0'
                  onKeyPress={handleNumberInput}
                  maxLength={MAX_NUMBER_INPUT_UPC}
                />
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item
              label='SKU'
              name='sku'
              rules={[{ required: true, message: 'SKU is required' }]}
            >
              <OverlayInputWrapper {...getOverlayProps('sku')}>
                <Input />
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item
              label='Model'
              name='model'
              rules={[{ required: true, message: 'Model is required' }]}
            >
              <OverlayInputWrapper {...getOverlayProps('model')}>
                <Input />
              </OverlayInputWrapper>
            </Form.Item>
          </Fieldset>

          <Fieldset title='Measurements'>
            <Form.Item
              label='Height in Inches'
              name='height'
              rules={[{ required: true, message: 'Height is required' }]}
            >
              <OverlayInputWrapper {...getOverlayProps('height')}>
                <InputNumber
                  style={styles.numericInput}
                  min={0}
                  max={MAX_NUMBER_INPUT_UPC}
                />
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item
              label='Width in Inches'
              name='width'
              rules={[{ required: true, message: 'Width is required' }]}
            >
              <OverlayInputWrapper {...getOverlayProps('width')}>
                <InputNumber
                  style={styles.numericInput}
                  min={0}
                  max={MAX_NUMBER_INPUT_UPC}
                />
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item
              label='Length in Inches'
              name='length'
              rules={[{ required: true, message: 'Length is required' }]}
            >
              <OverlayInputWrapper {...getOverlayProps('length')}>
                <InputNumber
                  style={styles.numericInput}
                  min={0}
                  max={MAX_NUMBER_INPUT_UPC}
                />
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item
              label='Weight in Pounds'
              name='weight'
              rules={[{ required: true, message: 'Weight is required' }]}
            >
              <OverlayInputWrapper {...getOverlayProps('weight')}>
                <InputNumber
                  style={styles.numericInput}
                  min={0}
                  max={MAX_NUMBER_INPUT_UPC}
                />
              </OverlayInputWrapper>
            </Form.Item>

            <Form.Item label='Stock Unit of Measure' name='stockUOM'>
              <OverlayInputWrapper {...getOverlayProps('stockUOM')}>
                <Input />
              </OverlayInputWrapper>
            </Form.Item>
          </Fieldset>
        </Col>

        <Col
          className='add-product__section-column add-product__section-column--full'
          span={8}
        >
          <Fieldset title='Marketing'>
            <Form.Item
              className='add-product__form-item--with-count'
              label='Short Description'
              name='shortDescription'
              rules={[
                {
                  required: true,
                  message: 'Short Description is required',
                },
              ]}
            >
              <Input.TextArea maxLength={30} rows={2} showCount />
            </Form.Item>

            <Form.Item
              className='add-product__form-item--with-count'
              label='Long Description'
              name='longDescription'
            >
              <Input.TextArea maxLength={90} rows={3} showCount />
            </Form.Item>

            <Form.Item
              label='Marketing Description'
              name='marketingDescription'
            >
              <Input.TextArea rows={3} />
            </Form.Item>

            <Form.Item label='Features' name='features'>
              <Input.TextArea rows={3} />
            </Form.Item>

            <Form.Item
              label='Additional Attributes For End Consumer'
              name='additionalAttributesForEndConsumer'
            >
              <OverlayInputWrapper
                {...getOverlayProps('additionalAttributesForEndConsumer')}
              >
                <Input.TextArea rows={2} />
              </OverlayInputWrapper>
            </Form.Item>

            <Row>
              <Col span={8} />
              <Col span={16}>
                <Form.Item name='brandId'>
                  <Form.Item>
                    <div className='add-product__thumb-wrapper'>
                      <div>
                        <CloseOutlined
                          className='add-product__thumb-wrapper-close-btn'
                          onClick={handleResetBrandId}
                        />
                      </div>
                      <ThumbnailItem
                        hideLabel={true}
                        imageThumbnail={brandThumb}
                        hideCheckbox
                        style={{ marginLeft: 0 }}
                      />
                    </div>
                    <Tooltip title='Choose a Brand ID'>
                      <Button onClick={() => setEditBrandVisible(true)}>
                        Link to Brand
                      </Button>
                    </Tooltip>
                  </Form.Item>
                </Form.Item>
                {brandError ? (
                  <Text type='danger'>{brandErrorMessage}</Text>
                ) : null}
                {editBrandVisible && (
                  <ProductEditBrandModal
                    visible={editBrandVisible}
                    setVisibleEditBrand={setEditBrandVisible}
                    productDetail={productData}
                    isAddProduct={true}
                    isEdit={isEdit}
                    setBrandId={setBrandId}
                    setBrandThumb={setBrandThumb}
                    memberIdFromBrand={memberIdBrand}
                  />
                )}
              </Col>
            </Row>
          </Fieldset>
        </Col>
      </Row>
    );
  };

  return (
    <Form
      form={form}
      {...formItemLayout}
      name='Add-Product'
      preserve={false}
      className='add-product__modal'
    >
      <FullWidthSpace size={16}>
        <Row gutter={16}>
          <Col span={24}>{renderProductFieldset()}</Col>
        </Row>
      </FullWidthSpace>
    </Form>
  );
};

const styles = {
  numericInput: {
    width: '100%',
  },
  fullWidthInput: {
    width: '100%',
  },
};

const MAX_NUMBER_INPUT_UPC = 9999999999999999999;

const formItemLayout = {
  labelCol: {
    xl: {
      span: 24,
    },
    xxl: {
      span: 8,
    },
  },
  wrapperCol: {
    xl: {
      span: 24,
    },
    xxl: {
      span: 15,
    },
  },
};

export default CreateProductForm;
